/**
 * ATL-1445 stage 3, P6 — merging this device's pending rows into `OperationsList` (plan §3 "Sales
 * list"; §9 risk 5 for clients).
 *
 * A sale rung while its master write is still in outbox v2 is not in `GET /sales` yet. In Firestore
 * mode the SDK shows it in the list at once; this merge does the same for a backend-first till:
 * pending rows (`localViewAtom`'s `rows`) are added to the list the server returned, filtered by the
 * list's range and store, and DE-DUPLICATED BY ID — wherever the server row is present it wins and the
 * pending row is not shown. The local view retires an acknowledged row exactly when a list's server
 * rows contain its id (`observeLocalViewRows`).
 *
 * Pure. With no pending rows — every session today — it returns the `sections` argument ITSELF, so the
 * list renders exactly master's data object.
 *
 * Placement keeps the server's indices valid. `OperationsList`'s detail screen edits the list in place
 * by `(sectionIndex, itemIndex)` into the SERVER data; so a merged section keeps its server section's
 * `index`, a section that holds only pending rows gets `index: -1`, and `serverPlacementOf` maps a
 * shown server row back to its server position. A pending row has no server position: its detail opens
 * read-only (no edit/remove callbacks), since there is nothing on the server to edit yet.
 */
import { format } from 'date-fns'

import type { PendingRow, RowsOverlay } from '../global/Write/localViewState'
import { idsOfRows, pendingRowInView } from '../global/Write/localViewOverlay'

export interface MergeableItem {
  id: string
  store?: string
  [key: string]: unknown
}

export interface MergeableSection<I extends MergeableItem> {
  title: string
  data: I[]
  index: number
}

/**
 * The pending rows a list of `collectionName` merges: `null` — not an empty array — whenever the local
 * view has no rows (every session today) or the collection has none to merge. `OperationsList`'s effects
 * depend on this value, so it must be the SAME value on every render of today's lists: a fresh `[]` per
 * render would re-run the total effect on every render and report the list's rows on every data change
 * (review 2). With rows it is the overlay's own array, stable until the local view changes.
 */
export function pendingRowsForList(localRows: RowsOverlay | null, collectionName: string): readonly PendingRow[] | null {
  return localRows !== null && (collectionName === 'sales' || collectionName === 'clients') ? localRows[collectionName] : null
}

/** The rows the merge added, by identity (never a field on the row: the row is the server's shape). */
const pendingItems = new WeakSet<object>()

export const isPendingRowItem = (item: object): boolean => pendingItems.has(item)

export interface PendingRowsView {
  store: string
  /** The list's instant range (`dateRangeWithTimezoneAndAutoclose`), or nulls for "all time". */
  startMs: number | null
  endMs: number | null
  /** The list's date field (`saleDate`, `registrationDate`). */
  dateField: string
  /** The store's local date of an instant — the list's own section key (`getLocalStoreDate`). */
  localDateOf: (store: string | undefined, at: Date) => Date
}

const SECTION_FORMAT = 'd/M/y EEEE'

function dateOfItem(item: MergeableItem, dateField: string): number | null {
  const value = item[dateField]
  if (value && typeof value === 'object' && typeof (value as { toDate?: unknown }).toDate === 'function') {
    const date = (value as { toDate: () => unknown }).toDate()
    return date instanceof Date && Number.isFinite(date.getTime()) ? date.getTime() : null
  }
  if (value instanceof Date) return Number.isFinite(value.getTime()) ? value.getTime() : null
  if (typeof value === 'number') return Number.isFinite(value) ? value : null
  return null
}

/** The pending row as a list item: the server row's shape, the date as a Timestamp-like. */
export function pendingRowToItem<I extends MergeableItem>(row: PendingRow, dateField: string): I {
  const at = row.date === null ? null : new Date(row.date)
  const item: MergeableItem = {
    ...(row.fields as Record<string, unknown>),
    id: row.id,
    ...(row.store !== null ? { store: row.store } : {}),
    ...(at !== null ? { [dateField]: { toDate: () => at } } : {}),
  }
  pendingItems.add(item)
  return item as I
}

/**
 * `sections` plus the pending rows in view that the server has not returned. Returns `sections` itself
 * when there is nothing to add.
 */
export function mergePendingRows<I extends MergeableItem>(
  sections: MergeableSection<I>[],
  pending: readonly PendingRow[] | null | undefined,
  view: PendingRowsView,
): MergeableSection<I>[] {
  if (!pending || pending.length === 0) return sections
  const serverIds = idsOfRows(sections.flatMap((section) => section.data))
  const toAdd = pending.filter((row) => !serverIds.has(row.id) && row.date !== null && pendingRowInView(row, view))
  if (toAdd.length === 0) return sections

  const merged = sections.map((section) => ({ ...section }))
  // Sections whose `data` array this merge owns. Tracked by OBJECT, not position: a section spliced in
  // shifts every later position, and a stale position would let a server array be mutated in place.
  const owned = new WeakSet<object>()
  // Newest first, as the list is.
  const sorted = [...toAdd].sort((a, b) => Date.parse(b.date as string) - Date.parse(a.date as string))
  for (const row of sorted) {
    const at = new Date(row.date as string)
    const item = pendingRowToItem<I>(row, view.dateField)
    const title = format(view.localDateOf(row.store ?? undefined, at), SECTION_FORMAT)
    const position = merged.findIndex((section) => section.title === title)
    if (position === -1) {
      const timestamp = at.getTime()
      const sectionDate = (s: MergeableSection<I>): number => {
        const first = s.data.find((i) => dateOfItem(i, view.dateField) !== null)
        return first ? (dateOfItem(first, view.dateField) as number) : Number.NEGATIVE_INFINITY
      }
      let insertAt = merged.findIndex((section) => sectionDate(section) < timestamp)
      if (insertAt === -1) insertAt = merged.length
      const created: MergeableSection<I> = { title, data: [item], index: -1 }
      owned.add(created)
      merged.splice(insertAt, 0, created)
      continue
    }
    let target = merged[position]
    if (!owned.has(target)) {
      target = { ...target, data: [...target.data] }
      owned.add(target)
      merged[position] = target
    }
    const data = target.data
    const before = data.findIndex((existing) => {
      const t = dateOfItem(existing, view.dateField)
      return t !== null && t < at.getTime()
    })
    data.splice(before === -1 ? data.length : before, 0, item)
  }
  return merged
}

/**
 * Where a SHOWN row lives in the server data: `{ sectionIndex, itemIndex }` for a server row, `null` for
 * a pending one. When nothing was merged (`shown === server`) it is the row's own `(section.index,
 * index)`, exactly as before.
 */
export function serverPlacementOf<I extends MergeableItem>(
  server: MergeableSection<I>[],
  shown: MergeableSection<I>[],
  section: MergeableSection<I>,
  item: I,
  index: number,
): { sectionIndex: number; itemIndex: number } | null {
  if (shown === server) return { sectionIndex: section.index, itemIndex: index }
  if (isPendingRowItem(item)) return null
  const serverSection = server[section.index]
  const itemIndex = serverSection ? serverSection.data.indexOf(item) : -1
  return itemIndex === -1 ? null : { sectionIndex: section.index, itemIndex }
}
